import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Coffee, Clock, BookOpen, Brain, Sparkles, Play, 
  CheckCircle, AlertTriangle, Timer, Heart, Eye
} from 'lucide-react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { useSessionControl } from '../contexts/SessionControlContext';

// Break Suggestion Modal
export const BreakSuggestionModal = () => {
  const navigate = useNavigate();
  const { 
    showBreakModal, 
    fatigueStatus, 
    startBreak, 
    dismissBreakSuggestion,
    thresholds
  } = useSessionControl();

  if (!showBreakModal || !fatigueStatus) return null;

  const handleTakeBreak = async () => {
    await startBreak();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        >
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-amber-400 to-orange-500 p-6 text-white text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Coffee className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Great Work!</h2>
            <p className="text-amber-100">Time for a well-deserved break</p>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-gray-50 rounded-lg p-3">
                <Clock className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                <p className="text-lg font-bold text-gray-800">
                  {Math.round(fatigueStatus.session_duration_minutes || 0)}m
                </p>
                <p className="text-xs text-gray-500">Session Time</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <Brain className="w-5 h-5 text-purple-500 mx-auto mb-1" />
                <p className="text-lg font-bold text-gray-800">
                  {Math.round(fatigueStatus.recent_accuracy || 0)}%
                </p>
                <p className="text-xs text-gray-500">Accuracy</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <Heart className="w-5 h-5 text-red-500 mx-auto mb-1" />
                <p className="text-lg font-bold text-gray-800">
                  {Math.round(fatigueStatus.fatigue_score || 0)}
                </p>
                <p className="text-xs text-gray-500">Focus Level</p>
              </div>
            </div>

            {/* Message */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-amber-800 text-sm">
                {fatigueStatus.message || "You've been focused for a while. A short break will help you stay sharp!"}
              </p>
            </div>

            {/* Break activities */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">During your break, you can:</p>
              <div className="flex flex-wrap gap-2">
                <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs flex items-center gap-1">
                  <Eye className="w-3 h-3" /> Review Answers
                </span>
                <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs flex items-center gap-1">
                  <BookOpen className="w-3 h-3" /> E-Library
                </span>
                <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Resources
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleTakeBreak}
                className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
              >
                <Coffee className="w-4 h-4 mr-2" />
                Take {thresholds.break_duration_minutes}min Break
              </Button>
              <Button
                onClick={dismissBreakSuggestion}
                variant="outline"
                className="flex-1"
              >
                <Play className="w-4 h-4 mr-2" />
                Continue
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Enforced Lock Modal
export const EnforcedLockModal = () => {
  const navigate = useNavigate();
  const { 
    showLockModal, 
    fatigueStatus, 
    startBreak,
    thresholds 
  } = useSessionControl();

  if (!showLockModal || !fatigueStatus) return null;

  const handleTakeBreak = async () => {
    await startBreak();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        >
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-red-500 to-pink-500 p-6 text-white text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Break Time!</h2>
            <p className="text-red-100">Your brain needs a rest to stay sharp</p>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Fatigue indicator */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Mental Fatigue Level</span>
                <span className="font-medium text-red-600">{Math.round(fatigueStatus.fatigue_score || 85)}%</span>
              </div>
              <Progress 
                value={fatigueStatus.fatigue_score || 85} 
                className="h-3 bg-gray-200"
              />
            </div>

            {/* Message */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm">
                {fatigueStatus.message || "You've been working hard! Taking a break now will help you learn better when you return."}
              </p>
            </div>

            {/* Science fact */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-800 text-sm font-medium mb-1">Did you know?</p>
              <p className="text-blue-700 text-xs">
                Studies show that taking regular breaks improves memory retention by up to 40%! 
                Your brain consolidates learning during rest.
              </p>
            </div>

            {/* What you can do */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">During your break:</p>
              <div className="grid grid-cols-2 gap-2">
                <button 
                  onClick={() => { handleTakeBreak(); navigate('/elibrary'); }}
                  className="bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg p-3 text-left transition-all"
                >
                  <BookOpen className="w-5 h-5 text-green-600 mb-1" />
                  <p className="text-sm font-medium text-green-800">E-Library</p>
                  <p className="text-xs text-green-600">Light reading</p>
                </button>
                <button 
                  onClick={() => { handleTakeBreak(); navigate('/resources'); }}
                  className="bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg p-3 text-left transition-all"
                >
                  <Sparkles className="w-5 h-5 text-purple-600 mb-1" />
                  <p className="text-sm font-medium text-purple-800">Resources</p>
                  <p className="text-xs text-purple-600">Review notes</p>
                </button>
              </div>
            </div>

            {/* Action */}
            <Button
              onClick={handleTakeBreak}
              className="w-full bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white"
            >
              <Coffee className="w-4 h-4 mr-2" />
              Start {thresholds.break_duration_minutes}-Minute Break
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// On Break Overlay (shown during break)
export const OnBreakOverlay = () => {
  const navigate = useNavigate();
  const { 
    fatigueStatus, 
    breakRemaining, 
    endBreak 
  } = useSessionControl();

  const [canResume, setCanResume] = useState(false);

  useEffect(() => {
    // Allow resume after break is complete
    if (breakRemaining <= 0 && fatigueStatus?.status === 'on_break') {
      setCanResume(true);
    }
  }, [breakRemaining, fatigueStatus]);

  if (!fatigueStatus || fatigueStatus.status !== 'on_break') return null;

  const minutes = Math.floor(breakRemaining);
  const seconds = Math.floor((breakRemaining - minutes) * 60);

  const handleResume = async () => {
    await endBreak();
  };

  return (
    <div className="fixed bottom-4 right-4 z-40">
      <motion.div
        initial={{ x: 100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 max-w-xs"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
            <Coffee className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="font-medium text-gray-800">Break Time</p>
            <p className="text-xs text-gray-500">Rest your mind</p>
          </div>
        </div>

        {!canResume ? (
          <>
            <div className="text-center mb-3">
              <div className="text-3xl font-bold text-amber-600">
                {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
              </div>
              <p className="text-xs text-gray-500">remaining</p>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate('/elibrary')}
                className="flex-1 text-xs"
              >
                <BookOpen className="w-3 h-3 mr-1" />
                E-Library
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate('/resources')}
                className="flex-1 text-xs"
              >
                <Sparkles className="w-3 h-3 mr-1" />
                Resources
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="text-center mb-3">
              <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
              <p className="text-sm text-gray-700">Break complete!</p>
            </div>
            <Button
              size="sm"
              onClick={handleResume}
              className="w-full bg-green-600 hover:bg-green-700 text-white"
            >
              <Play className="w-4 h-4 mr-2" />
              Resume Learning
            </Button>
          </>
        )}
      </motion.div>
    </div>
  );
};

// Combined Session Control UI (include this in App.js)
// Session Status Bar - subtle floating indicator during active sessions
export const SessionStatusBar = () => {
  const { 
    isActive, 
    fatigueStatus, 
    sessionId,
    sscsEnabled,
    breakRemaining
  } = useSessionControl();
  
  if (!isActive || !sscsEnabled || !sessionId || fatigueStatus?.status === 'on_break') return null;
  
  const duration = fatigueStatus?.session_duration_minutes || 0;
  const fatigue = fatigueStatus?.fatigue_score || 0;
  const accuracy = fatigueStatus?.recent_accuracy;
  
  // Don't show until at least 1 minute into session
  if (duration < 1) return null;
  
  const getBarColor = () => {
    if (fatigue >= 60) return 'bg-red-500';
    if (fatigue >= 40) return 'bg-amber-500';
    return 'bg-green-500';
  };
  
  const getStatusEmoji = () => {
    if (fatigue >= 60) return '🔴';
    if (fatigue >= 40) return '🟡';
    return '🟢';
  };
  
  const formatTime = (mins) => {
    const m = Math.floor(mins);
    const s = Math.round((mins - m) * 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div 
      className="fixed bottom-4 right-4 z-40 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl shadow-lg px-4 py-2.5 flex items-center gap-3 text-xs"
      data-testid="session-status-bar"
    >
      <div className="flex items-center gap-1.5">
        <span>{getStatusEmoji()}</span>
        <span className="font-medium text-gray-700">{formatTime(duration)}</span>
      </div>
      <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full transition-all duration-500 ${getBarColor()}`} 
          style={{ width: `${Math.min(100, fatigue)}%` }}
        />
      </div>
      {accuracy !== undefined && accuracy !== null && (
        <span className="text-gray-500">{Math.round(accuracy)}% acc</span>
      )}
    </div>
  );
};

export const SessionControlUI = () => {
  return (
    <>
      <BreakSuggestionModal />
      <EnforcedLockModal />
      <OnBreakOverlay />
      <SessionStatusBar />
    </>
  );
};

export default SessionControlUI;
